import { useCallback, useEffect, useRef, useState, type SetStateAction } from "react";

import { Cart } from "../../types/cart";
import { CheckoutTotal } from "../../types/checkout";
import { ShopFull } from "../../types/retailShop";
import useAdminShop from "../useAdminShop";
import useRetailShop from "../useRetailShop";
import { useCalculateTotal } from "./useCalculateTotal.hook";
import { usePosCheckout } from "./useFetchCheckout";

export default function useCheckoutTotal({
  cart,
  userId,
  orderOverrideDiscount,
  orderDiscountedPrice,
}: {
  cart: Cart;
  userId: string;
  orderOverrideDiscount?: number;
  orderDiscountedPrice?: number;
}) {
  const adminShop = useAdminShop();
  const retailShop = useRetailShop();
  const rewardPointsEnabled = retailShop?.useRewardPoints !== false;
  const [useRewardPoints, setUseRewardPointsInternal] = useState(0);
  const userAdjustedRewardRef = useRef(false);
  const lastRewardSyncUserIdRef = useRef(userId);

  const { data: checkoutSession, isLoading, isRefetching } = usePosCheckout();
  const calculateTotalMutation = useCalculateTotal();

  const setUseRewardPoints = useCallback((action: SetStateAction<number>) => {
    userAdjustedRewardRef.current = true;
    setUseRewardPointsInternal(action);
  }, []);

  const redeemable = checkoutSession?.checkoutTotal?.redeemableRewardTotal;
  const userRewardPts = checkoutSession?.checkoutTotal?.userRewardPoints;
  const orderOv = checkoutSession?.checkoutTotal?.orderOverrideDiscount;

  useEffect(() => {
    if (!rewardPointsEnabled) {
      setUseRewardPointsInternal(0);
      return;
    }

    const total = checkoutSession?.checkoutTotal;
    const userJustChanged = lastRewardSyncUserIdRef.current !== userId;
    if (userJustChanged) {
      lastRewardSyncUserIdRef.current = userId;
      userAdjustedRewardRef.current = false;
      setUseRewardPointsInternal(0);
      return;
    }

    if (!total || !userId || (userRewardPts ?? 0) <= 0 || (orderOv ?? 0) > 0) {
      setUseRewardPointsInternal(0);
      return;
    }

    const cap = typeof redeemable === "number" && redeemable >= 0 ? redeemable : 0;
    setUseRewardPointsInternal((prev) => {
      if (!userAdjustedRewardRef.current) {
        return cap;
      }
      return Math.min(Math.max(0, prev), cap);
    });
  }, [checkoutSession?.checkoutTotal, orderOv, redeemable, rewardPointsEnabled, userId, userRewardPts]);

  const rewardValue = rewardPointsEnabled
    ? getUsableReward(adminShop!, useRewardPoints, checkoutSession?.checkoutTotal)
    : 0;

  const items = cart?.items.map((c) => {
    let attachToIdx = cart.items.findIndex((ci) => ci.id === c.attachCartItemId);

    return {
      attachToIdx: attachToIdx === -1 ? undefined : attachToIdx,
      customization: c.customization,
      overridePrice: c.overridePrice,
      price: c.itemPrice * c.quantity,
      productId: c.product.id,
      productNote: c.productNote,
      quantity: c.quantity,
      topUps: c.topUps,
      warehouseId: c.warehouseId,
    };
  });

  useEffect(() => {
    if (cart?.items && cart.items.length > 0) {
      calculateTotalMutation.mutate({
        items,
        userId,
        usedReward: rewardValue,
        orderOverrideDiscount,
        orderDiscountedPrice,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart?.items, rewardValue, userId, orderOverrideDiscount, orderDiscountedPrice]);

  return {
    checkoutTotal: checkoutSession?.checkoutTotal,
    isRevalidating: isLoading || isRefetching || calculateTotalMutation.isPending,
    useRewardPoints,
    setUseRewardPoints,
    rewardValue,
  };
}

function calculateTotalDeductible(shop: ShopFull, itemsTotal: number, tax: number): number {
  if (!shop) {
    return 0;
  }

  let totalDeductible: number = 0;
  if (shop.rewardAppliesTo.includes("order")) {
    totalDeductible += itemsTotal;
  }
  if (shop.rewardAppliesTo.includes("tax")) {
    totalDeductible += tax;
  }
  if (shop.minOrderAmtAfterDeduct > 0) {
    totalDeductible -= shop.minOrderAmtAfterDeduct;
  }

  return totalDeductible;
}

export const getUsableReward = (shop: ShopFull, useRewardPoints: number, total?: CheckoutTotal): number => {
  if (!total || !Number.isFinite(useRewardPoints) || useRewardPoints <= 0) {
    return 0;
  }

  const { itemsTotal, tax, redeemableRewardTotal } = total;
  const totalDeductible = calculateTotalDeductible(shop, itemsTotal, tax);

  const maxApplicable = total.userRewardPoints > totalDeductible ? totalDeductible : total.userRewardPoints;

  const capByRedeemable =
    typeof redeemableRewardTotal === "number" && redeemableRewardTotal >= 0
      ? Math.min(maxApplicable, redeemableRewardTotal)
      : maxApplicable;

  return Math.min(useRewardPoints, capByRedeemable);
};
